// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { cn } from '@/lib/utils';
import { hueFor } from '@/lib/hueFor';

import { brandForCatalogKey, brandForSlug } from './integrationBrands';

export interface IntegrationTileProps {
  slug: string;
  displayName: string;
  /**
   * The integration's catalog entry key (ADR-0026), when registered from the catalog.
   * Resolves the logo via `catalog_key → logo_ref → bundled asset`; a Custom
   * (catalog-less) integration falls back to the slug registry, then a monogram.
   */
  catalogKey?: string | null;
  /** Tile side in px. Default 40. The inspect header uses 46. */
  size?: number;
  className?: string;
}

/** Decorative SVG globe/link glyph for the generic variant. Uses currentColor. */
function GenericGlyph(): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="8"
        cy="8"
        r="6.3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse
        cx="8"
        cy="8"
        rx="2.8"
        ry="6.3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="1.7"
        y1="5.6"
        x2="14.3"
        y2="5.6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <line
        x1="1.7"
        y1="10.4"
        x2="14.3"
        y2="10.4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Square app-tile rendering a brand mark, monogram, or generic glyph.
 * The tile is purely decorative — the adjacent integration name carries meaning.
 */
export function IntegrationTile({
  slug,
  displayName,
  catalogKey,
  size = 40,
  className,
}: IntegrationTileProps): React.JSX.Element {
  const brand = catalogKey
    ? brandForCatalogKey(catalogKey, displayName)
    : brandForSlug(slug, displayName);
  const imgSize = Math.round(size * 0.58);

  if (brand.kind === 'brand') {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex shrink-0 items-center justify-center overflow-hidden border border-border bg-card rounded-gl-icon',
          className,
        )}
        style={{ width: size, height: size }}
      >
        {/* When a dark variant exists, swap by theme via CSS (no JS theme read). */}
        <img
          src={brand.src}
          alt=""
          width={imgSize}
          height={imgSize}
          aria-hidden="true"
          className={brand.darkSrc ? 'dark:hidden' : undefined}
        />
        {brand.darkSrc && (
          <img
            src={brand.darkSrc}
            alt=""
            width={imgSize}
            height={imgSize}
            aria-hidden="true"
            className="hidden dark:block"
          />
        )}
      </span>
    );
  }

  if (brand.kind === 'mono') {
    // Brand-tinted, deterministic hue with white text — AA-contrast on both themes
    // (same color math as MonogramTile). Reads as an icon, not a dull gray square.
    const h = hueFor(brand.seed);
    return (
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-gl-icon font-mono font-semibold text-white',
          className,
        )}
        style={{
          width: size,
          height: size,
          fontSize: Math.round(size * 0.38),
          backgroundColor: `hsl(${h} 50% 30%)`,
        }}
      >
        {brand.letter}
      </span>
    );
  }

  // generic
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden border border-border bg-muted rounded-gl-icon text-verdant',
        className,
      )}
      style={{ width: size, height: size }}
    >
      <GenericGlyph />
    </span>
  );
}
